import {
    Component,
    HostListener,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewChild,
    NgZone,
    AfterViewInit,
    Output,
    EventEmitter,
    TemplateRef
} from '@angular/core';
import { IonContent, IonModal, ModalController } from '@ionic/angular';
import { Capacitor } from '@capacitor/core';
import { DOCUMENT } from '@angular/common';
import Env from '@ridango/core/env';
import { take, takeUntil } from 'rxjs/operators';
import { BaseComponent } from '@ridango/core';
import { MapApiService } from '../map/services';
import { ApplicationFacade, OpenTripPlannerFacade } from '@ridango/components';
import { BreakpointView, SmallScreenSheetModalContainerService } from './small-screen-sheet-modal-container.service';
import { IonModalService } from '@ridango/core/lib/services/ion-modal.service';
import { combineLatest, Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'rwidget-small-screen-sheet-modal-container',
    templateUrl: './small-screen-sheet-modal-container.component.html',
    styleUrls: ['./small-screen-sheet-modal-container.component.scss']
})
export class SmallScreenSheetModalContainerComponent extends BaseComponent implements OnInit, OnDestroy, AfterViewInit {
    private _fullscreen = false;
    @Input() minimalBreakpointOffset = 0;
    @Input() initialBreakpointIndex = BreakpointView.Middle;
    @Input() rollUpOnMapClick = false;
    @Input() isFullHeightContainer = false;
    @Input() isShowAdditionalBreakpoint = false;
    @Input() set fullscreen(value: boolean) {
        this._fullscreen = value;
        this.setupBreakpoints();
    }
    @Output() fullscreenChange = new EventEmitter<boolean>();

    initialBreakpoint;

    @Output() didPresent = new EventEmitter();
    @ViewChild(IonModal) modal: IonModal;
    @ViewChild('scrollContainer') content: IonContent;
    readonly mobileViewBreakPoint = 768;
    headerTemplate: TemplateRef<unknown>;
    screenWidth: number;
    mapMoved = false;
    breakpoints: number[];
    componentDestroyed = false;
    isModalFullHeight = false;
    private navMenu = this.document.querySelector('ion-footer');
    // Removed pulltorefresh since it is not used anymore, but made this comment for future reference
    atTop = true;
    private touchStartY = 0;
    rubberOffset = 0;
    private maxPull = 200; // px, max distance to pull down, maybe not needed
    private isDragging = false;
    private threshold = 80; // when to snap to lower breakpoint
    private currentY = 0;
    private availableBreakpoints: number[] = [];

    @HostListener('window:resize', ['$event'])
    onResize() {
        this.screenWidth = window.innerWidth;
        this.toggleSiteFooterVisibility();
        this.setupBreakpoints();
        this.handleMapDrag();
    }

    constructor(
        @Inject(DOCUMENT) private document: Document,
        @Inject('ENV') public env: Env,
        private renderer: Renderer2,
        private mapApi: MapApiService,
        private zone: NgZone,
        private applicationFacade: ApplicationFacade,
        private ionModalService: IonModalService,
        private openTripPlannerFacade: OpenTripPlannerFacade,
        private modalCtrl: ModalController,
        private smallScreenSheetModalContainerService: SmallScreenSheetModalContainerService,
        private translate: TranslateService
    ) {
        super();
    }

    ngOnInit(): void {
        this.screenWidth = window.innerWidth;
        this.toggleSiteFooterVisibility();
        this.setupBreakpoints();

        this.ionModalService.scrollToTop$.pipe(takeUntil(this.componentDestroyed$)).subscribe(() => {
            this.scrollToTop();
        });

        this.zone.runOutsideAngular(() => {
            this.mapApi
                .addListenerSubscription('dragstart')
                ?.pipe(takeUntil(this.componentDestroyed$))
                .subscribe(() => {
                    this.zone.run(async () => {
                        if (
                            (await this.modal?.getCurrentBreakpoint()) !== this.modal.breakpoints[BreakpointView.Small]
                        ) {
                            this.mapMoved = false;
                        }
                        if (!this.mapMoved) {
                            this.handleMapDrag();
                        }
                        return;
                    });
                });
        });

        if (this.rollUpOnMapClick) {
            this.zone.runOutsideAngular(() => {
                this.mapApi
                    .addListenerSubscription('click')
                    ?.pipe(takeUntil(this.componentDestroyed$))
                    .subscribe(() => {
                        this.zone.run(async () => {
                            await this.modal?.setCurrentBreakpoint(this.breakpoints[BreakpointView.Small]);
                        });
                    });
            });
        }

        this.smallScreenSheetModalContainerService.broadcastTriggerCalculateBreakpoint$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(this.subscriberSetBreakpoint.bind(this));

        this.setInitialModalPosition();

        this.smallScreenSheetModalContainerService.headerTemplate$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((template) => {
                if (template == null) this.content?.scrollToTop();
                this.headerTemplate = template;
            });
    }

    ngOnDestroy(): void {
        if (this.modal) {
            this.modal.animated = false;
            this.modal.dismiss();
        }
        this.componentDestroyed = true;
        super.ngOnDestroy();
    }

    ngAfterViewInit(): void {
        if (!this.modal) {
            return;
        }

        this.modal.didPresent.pipe(take(1)).subscribe((_event) => {
            if (this.componentDestroyed) {
                this.modal.dismiss();
            }
        });
    }

    onScroll(ev: CustomEvent) {
        this.atTop = (ev.detail.scrollTop || 0) <= 0;
    }

    onTouchStart(event: TouchEvent) {
        if (!this.atTop) return;

        this.touchStartY = event.touches[0].clientY;
        this.isDragging = true;
    }

    onTouchMove(event: TouchEvent) {
        if (!this.isDragging || !this.atTop) return;

        this.currentY = event.touches[0].clientY;
        const deltaY = this.currentY - this.touchStartY;

        if (deltaY < 0) return; // no upward dragging

        const limitedY = Math.min(deltaY, this.maxPull);

        // Applying drag effect, multiplying by 0.5 for a smoother effect
        this.rubberOffset = limitedY * 0.5;
    }

    onTouchEnd() {
        if (!this.isDragging) return;
        this.isDragging = false;

        // Calculate the distance dragged
        const deltaY = this.currentY - this.touchStartY;

        // Check if the drag distance exceeds the threshold
        if (deltaY > this.threshold) {
            this.lowerBreakpoint();
        }
        // Reset transform
        if (this.rubberOffset > 0) {
            this.rubberOffset = 0;
        }
    }

    async lowerBreakpoint() {
        const modal = await this.modalCtrl.getTop();
        // Currently, this is for fullscreen modal only since there is scrolling in the modal.
        const bp = await this.modal.getCurrentBreakpoint();
        if (bp === 1) {
            modal.setCurrentBreakpoint(this.breakpoints[this.breakpoints.length - 2]);
        }
    }

    onModalPresent() {
        const modalEl = this.document.querySelector('#modal');
        this.allowScreenReaderToReachFooter(modalEl);
        // Apply screen reader metadata before the modal is shown, but delay actual focus until presentation finishes.
        this.modalHandleForScreenReader(modalEl);
    }

    private modalHandleForScreenReader(modalEl: Element): void {
        const modalHandleBtn = modalEl.shadowRoot?.querySelector('.modal-handle') as HTMLElement;
        if (modalHandleBtn) {
            modalHandleBtn.setAttribute('aria-hidden', 'false');
            modalHandleBtn.setAttribute('aria-label', this.translate.instant('SHEET_MODAL_ADJUSTMENT_BTN_LABEL'));
            modalHandleBtn.setAttribute('aria-expanded', String(this.isModalFullHeight));
        }
    }

    private setInitialModalPosition(): void {
        combineLatest([this.ionModalService.breakpoints$, this.openTripPlannerFacade.location$])
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(([breakpoints, location]) => {
                this.availableBreakpoints = breakpoints;
                if (!breakpoints.length) {
                    return;
                }

                if (location.hasError && this.isShowAdditionalBreakpoint) {
                    const alertLocationMessageOffset = 80;
                    const additionalBreakpoint = this.calculateBreakpoint(
                        this.navMenu?.clientHeight ?? 0,
                        this.minimalBreakpointOffset + alertLocationMessageOffset
                    );

                    breakpoints.push(additionalBreakpoint);
                    this.initialBreakpoint = breakpoints[breakpoints.length - 1];
                    Promise.resolve()
                        .then(() => {
                            const envBreakpointIndex = this.env.EXPLORE?.BREAKPOINT_INDEX;
                            if (envBreakpointIndex != null && envBreakpointIndex < breakpoints.length) {
                                this.modal?.setCurrentBreakpoint(breakpoints[envBreakpointIndex]);
                            } else {
                                this.modal?.setCurrentBreakpoint(this.initialBreakpoint);
                            }
                        })
                        .then(() => {
                            const additionalBreakpointIndex = breakpoints.indexOf(additionalBreakpoint);
                            if (additionalBreakpointIndex > -1) {
                                breakpoints.splice(additionalBreakpointIndex, 1);
                            }
                        });
                } else {
                    this.initialBreakpoint = breakpoints[this.initialBreakpointIndex] || breakpoints[0];
                }
            });
    }

    public goToBreakpoint(index: number): void {
        if (!this.availableBreakpoints?.length) return;

        const breakpoint = this.availableBreakpoints[Math.min(index, this.availableBreakpoints.length - 1)];

        Promise.resolve().then(() => this.modal?.setCurrentBreakpoint(breakpoint));
    }

    scrollToTop(): void {
        if (!this.content) return;
        this.content.scrollToTop();
    }

    async subscriberSetBreakpoint(breakpoint: number): Promise<void> {
        await this.modal?.setCurrentBreakpoint(this.breakpoints[breakpoint]);
    }

    handleMapDrag() {
        this.mapMoved = true;
        this.modal?.setCurrentBreakpoint(this.breakpoints[BreakpointView.Small]);
    }

    setupBreakpoints() {
        if (this._fullscreen) {
            this.breakpoints = [1.0];
            if (!this.initialBreakpoint) {
                this.initialBreakpoint = 1.0;
            }
            return;
        }

        if (this.minimalBreakpointOffset === 0) {
            return;
        }

        const createBreakpoints = (clientHeight: number) => {
            const smallBreakpoint = this.calculateBreakpoint(clientHeight, this.minimalBreakpointOffset);
            this.breakpoints = [smallBreakpoint, 0.5, 1.0];
            this.ionModalService.setBreakpoints(this.breakpoints);
        };

        this.navMenu
            ? this.navMenu?.componentOnReady().then((element) => {
                  this.observeFooterChanges(element)
                      .pipe(takeUntil(this.componentDestroyed$))
                      .subscribe((element) => createBreakpoints(element.clientHeight));
              })
            : createBreakpoints(0);
    }

    private observeFooterChanges(element: HTMLElement): Observable<HTMLElement> {
        return new Observable<HTMLElement>((observer) => {
            if (element.clientHeight > 0) {
                observer.next(element);
                observer.complete();
                return;
            }

            const mutationObserver = new MutationObserver(() => {
                if (element.clientHeight > 0) {
                    observer.next(element);
                    mutationObserver.disconnect();
                    observer.complete();
                }
            });

            mutationObserver.observe(element, {
                attributes: true,
                childList: true,
                subtree: true
            });

            return () => {
                mutationObserver.disconnect();
            };
        });
    }

    private calculateBreakpoint(clientHeight: number, offset: number): number {
        return (clientHeight + offset) / window.innerHeight;
    }

    isMobileView(): boolean {
        return this.screenWidth < this.mobileViewBreakPoint;
    }

    isMobileHeaderMenuOpen(): boolean {
        return this.applicationFacade.isMenuOpen();
    }

    getAspectRatio(): number {
        return window.innerWidth / window.innerHeight;
    }

    private toggleSiteFooterVisibility() {
        if (Capacitor.isNativePlatform()) {
            return;
        }

        const element = this.document.querySelector('.site-footer');

        if (!element) {
            return;
        }

        if (this.isMobileView()) {
            this.renderer.addClass(element, 'hidden');
        } else {
            this.renderer.removeClass(element, 'hidden');
        }
    }

    adjustModalSize(): void {
        this.modal.getCurrentBreakpoint().then((breakpoint) => {
            if (breakpoint < this.breakpoints[BreakpointView.Full]) {
                this.isModalFullHeight = true;
                this.modal?.setCurrentBreakpoint(this.breakpoints[BreakpointView.Full]);
            } else {
                this.isModalFullHeight = false;
                this.modal?.setCurrentBreakpoint(this.breakpoints[BreakpointView.Small]);
            }
        });
    }

    onModalDidPresent() {
        const modalEl = this.document.querySelector<HTMLElement>('#modal');
        // Re-apply after didPresent: on iOS the overlay setup can restore aria-modal during the present animation.
        this.allowScreenReaderToReachFooter(modalEl);
        const modalHandleBtn = modalEl?.shadowRoot?.querySelector<HTMLElement>('.modal-handle');
        if (modalHandleBtn) {
            // Focus after didPresent so Ionic overlay setup does not override the intended initial focus target.
            modalHandleBtn.setAttribute('aria-live', 'assertive');
            modalHandleBtn.focus({ preventScroll: true });
        }

        this.didPresent.emit(true);
    }

    onModalBreakpointDidChange(ev: CustomEvent) {
        const isFullscreen = ev.detail?.['breakpoint'] === 1.0;
        this.fullscreenChange.emit(isFullscreen);
    }

    private allowScreenReaderToReachFooter(modalEl: Element) {
        const platform = Capacitor.getPlatform();
        if (platform !== 'android' && platform !== 'ios') return;

        if (modalEl) {
            modalEl.removeAttribute('aria-modal');

            if (modalEl.shadowRoot) {
                const modalWrapper = modalEl.shadowRoot.querySelector('.modal-wrapper');
                if (modalWrapper) {
                    modalWrapper.removeAttribute('aria-modal');
                }
            }
        }
    }
}
